import { create } from 'zustand'
import EncryptionService from '../utils/encryption'
import AuditLogger from '../utils/auditLogger'
import AuthService from '../services/authService'
import emailService from '../services/emailService'

// Store global de l'application avec sécurité
export const useStore = create((set, get) => ({
  // Language
  language: 'fr',
  setLanguage: (lang) => set({ language: lang }),

  // Theme/UI customization
  customization: {
    primaryColor: '#0ea5e9',
    accentColor: '#d946ef',
    glassmorphismIntensity: 0.1,
  },
  setCustomization: (custom) => set((state) => ({
    customization: { ...state.customization, ...custom }
  })),

  // Navigation
  isMenuOpen: false,
  toggleMenu: () => set((state) => ({ isMenuOpen: !state.isMenuOpen })),
  closeMenu: () => set({ isMenuOpen: false }),

  // Modal
  activeModal: null,
  modalData: null,
  openModal: (modalName, data = null) => set({ 
    activeModal: modalName, 
    modalData: data 
  }),
  closeModal: () => set({ activeModal: null, modalData: null }),

  // Residences filters
  residenceFilters: {
    type: 'all',
    status: 'all',
    priceRange: [0, 1000000],
    surface: [0, 200],
  },
  setResidenceFilters: (filters) => set((state) => ({
    residenceFilters: { ...state.residenceFilters, ...filters }
  })),

  // Gallery
  selectedGalleryCategory: 'all',
  setSelectedGalleryCategory: (category) => set({ 
    selectedGalleryCategory: category 
  }),

  // Loading states
  isLoading: false,
  setLoading: (loading) => set({ isLoading: loading }),

  // Notifications
  notifications: [],
  addNotification: (notification) => set((state) => ({
    notifications: [
      ...state.notifications,
      { id: Date.now(), ...notification }
    ]
  })),
  removeNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(n => n.id !== id)
  })),

  // 3D Scene Settings
  sceneSettings: {
    enableAnimations: true,
    rotationSpeed: 0.5,
    quality: 'high', // 'low', 'medium', 'high'
  },
  setSceneSettings: (settings) => set((state) => ({
    sceneSettings: { ...state.sceneSettings, ...settings }
  })),

  // User preferences
  preferences: {
    showAnimations: true,
    reducedMotion: false,
    autoPlayVideos: true,
  },
  setPreferences: (prefs) => set((state) => ({
    preferences: { ...state.preferences, ...prefs }
  })),

  // Authentication sécurisée
  user: null,
  isAuthenticated: false,
  
  login: async (credentialsOrSession) => {
    try {
      let session
      
      // Si c'est déjà un objet session (avec id, role, etc.), l'utiliser directement
      if (credentialsOrSession.id && credentialsOrSession.role) {
        session = credentialsOrSession
      } else {
        // Sinon, c'est des credentials, authentifier via AuthService
        session = await AuthService.authenticate(credentialsOrSession)
      }
      
      set({ user: session, isAuthenticated: true })
      
      await AuditLogger.log({
        action: 'USER_LOGIN',
        user: session.email,
        details: { sessionId: session.sessionId || 'simple-session' }
      })
      
      return session
    } catch (error) {
      await AuditLogger.log({
        action: 'LOGIN_ERROR',
        user: credentialsOrSession.email,
        details: { error: error.message },
        severity: 'ERROR'
      })
      throw error
    }
  },
  
  logout: async () => {
    const { user } = get()
    
    // Effacer la session simplifiée
    localStorage.removeItem('admin_session_simple')
    
    // Effacer la session normale
    await AuthService.logout()
    
    await AuditLogger.log({
      action: 'USER_LOGOUT',
      user: user?.email
    })
    
    set({ user: null, isAuthenticated: false })
  },
  
  checkAuth: () => {
    // Vérifier d'abord la session simplifiée
    const simpleSession = localStorage.getItem('admin_session_simple')
    if (simpleSession) {
      try {
        const session = JSON.parse(simpleSession)
        set({ user: session, isAuthenticated: true })
        
        // Charger les messages au démarrage
        get().loadMessages()
        return
      } catch (error) {
        console.error('Erreur parsing session simple:', error)
        localStorage.removeItem('admin_session_simple')
      }
    }

    // Sinon, vérifier la session normale
    const session = AuthService.getSession()
    if (session) {
      set({ user: session, isAuthenticated: true })
      
      // Charger les messages au démarrage
      get().loadMessages()
    } else {
      set({ user: null, isAuthenticated: false })
    }
  },
  
  // Stockage sécurisé
  secureStorage: {
    set: (key, value) => {
      return EncryptionService.secureStore(key, value)
    },
    
    get: (key) => {
      return EncryptionService.secureRetrieve(key)
    },
    
    remove: (key) => {
      EncryptionService.secureRemove(key)
    }
  },

  // Admin Data Management
  banners: (() => {
    try {
      const saved = localStorage.getItem('banners')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })(),
  jobPostings: (() => {
    try {
      const saved = localStorage.getItem('job_postings')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })(),
  applications: (() => {
    try {
      const saved = localStorage.getItem('applications')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })(),
  messages: [], // Messages from contact form
  galleryImages: (() => {
    try {
      const saved = localStorage.getItem('gallery_images')
      return saved ? JSON.parse(saved) : {
        events: [],
        residences: [],
        activities: [],
        team: []
      }
    } catch {
      return {
        events: [],
        residences: [],
        activities: [],
        team: []
      }
    }
  })(),
  
  // Banner Management
  addBanner: (banner) => set((state) => {
    const newBanner = { id: Date.now(), ...banner }
    const updatedBanners = [...state.banners, newBanner]
    localStorage.setItem('banners', JSON.stringify(updatedBanners))
    return { banners: updatedBanners }
  }),
  updateBanner: (id, updates) => set((state) => {
    const updatedBanners = state.banners.map(b => b.id === id ? { ...b, ...updates } : b)
    localStorage.setItem('banners', JSON.stringify(updatedBanners))
    return { banners: updatedBanners }
  }),
  deleteBanner: (id) => set((state) => {
    const updatedBanners = state.banners.filter(b => b.id !== id)
    localStorage.setItem('banners', JSON.stringify(updatedBanners))
    return { banners: updatedBanners }
  }),

  // Job Management
  addJobPosting: (job) => set((state) => {
    const newJob = { id: Date.now(), ...job }
    const updatedJobs = [...state.jobPostings, newJob]
    localStorage.setItem('job_postings', JSON.stringify(updatedJobs))
    return { jobPostings: updatedJobs }
  }),
  updateJobPosting: (id, updates) => set((state) => {
    const updatedJobs = state.jobPostings.map(j => j.id === id ? { ...j, ...updates } : j)
    localStorage.setItem('job_postings', JSON.stringify(updatedJobs))
    return { jobPostings: updatedJobs }
  }),
  deleteJobPosting: (id) => set((state) => {
    const updatedJobs = state.jobPostings.filter(j => j.id !== id)
    localStorage.setItem('job_postings', JSON.stringify(updatedJobs))
    return { jobPostings: updatedJobs }
  }),
  addApplication: (application) => set((state) => ({
    applications: [...state.applications, { id: Date.now(), ...application }]
  })),
  updateApplication: (id, updates) => set((state) => ({
    applications: state.applications.map(a => a.id === id ? { ...a, ...updates } : a)
  })),

  // Message Management
  addMessage: (message) => {
    const newMessage = {
      id: Date.now(),
      ...message,
      createdAt: new Date().toISOString(),
      status: 'unread',
      replies: []
    }
    
    set((state) => ({
      messages: [newMessage, ...state.messages]
    }))
    
    // Trigger notification for admins
    set((state) => ({
      notifications: [
        ...state.notifications,
        {
          id: `msg-${newMessage.id}`,
          type: 'message',
          title: 'Nouveau message reçu',
          message: `Message de ${message.name}: ${message.subject}`,
          timestamp: Date.now(),
          isRead: false,
          data: { messageId: newMessage.id }
        }
      ]
    }))
    
    // Save to localStorage
    const currentMessages = JSON.parse(localStorage.getItem('contact_messages') || '[]')
    localStorage.setItem('contact_messages', JSON.stringify([newMessage, ...currentMessages]))
    
    return newMessage
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map(m => m.id === id ? { ...m, ...updates } : m)
    }))
    
    // Update localStorage
    const currentMessages = JSON.parse(localStorage.getItem('contact_messages') || '[]')
    const updatedMessages = currentMessages.map(m => m.id === id ? { ...m, ...updates } : m)
    localStorage.setItem('contact_messages', JSON.stringify(updatedMessages))
  },

  deleteMessage: (id) => set((state) => ({
    messages: state.messages.filter(m => m.id !== id)
  })),

  addMessageReply: (messageId, reply) => {
    const newReply = {
      id: Date.now(),
      ...reply,
      createdAt: new Date().toISOString(),
      adminId: get().user?.email || 'unknown'
    }
    
    set((state) => ({
      messages: state.messages.map(m => 
        m.id === messageId 
          ? { ...m, replies: [...m.replies, newReply], status: 'replied' }
          : m
      )
    }))
    
    // Update localStorage
    const currentMessages = JSON.parse(localStorage.getItem('contact_messages') || '[]')
    const updatedMessages = currentMessages.map(m => {
      if (m.id === messageId) {
        return {
          ...m,
          replies: [...(m.replies || []), newReply],
          status: 'replied'
        }
      }
      return m
    })
    localStorage.setItem('contact_messages', JSON.stringify(updatedMessages))
  },

  loadMessages: () => {
    try {
      const storedMessages = localStorage.getItem('contact_messages')
      if (storedMessages) {
        const messages = JSON.parse(storedMessages)
        set({ messages })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error)
    }
  },

  markNotificationAsRead: (notificationId) => set((state) => ({
    notifications: state.notifications.map(n => 
      n.id === notificationId ? { ...n, isRead: true } : n
    )
  })),

  // Gallery Management
  addGalleryImage: (category, image) => set((state) => {
    const newImage = { id: Date.now(), ...image }
    const updatedImages = {
      ...state.galleryImages,
      [category]: [...state.galleryImages[category], newImage]
    }
    
    // Persister dans localStorage
    localStorage.setItem('gallery_images', JSON.stringify(updatedImages))
    
    return { galleryImages: updatedImages }
  }),
  deleteGalleryImage: (category, id) => set((state) => {
    const updatedImages = {
      ...state.galleryImages,
      [category]: state.galleryImages[category].filter(img => img.id !== id)
    }
    
    // Persister dans localStorage
    localStorage.setItem('gallery_images', JSON.stringify(updatedImages))
    
    return { galleryImages: updatedImages }
  }),

  // Admin Management
  addAdmin: async (adminData) => {
    try {
      // Envoyer l'email de demande d'approbation
      await emailService.sendAdminApprovalRequest(adminData)
      
      const success = AuthService.addAdmin(adminData)
      if (success) {
        console.log('Demande d\'admin envoyée avec succès')
      }
      return success
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande d\'admin:', error)
      throw error
    }
  },

  removeAdmin: async (email) => {
    try {
      const success = AuthService.removeAdmin(email)
      if (success) {
        console.log('Admin supprimé avec succès')
      }
      return success
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'admin:', error)
      throw error
    }
  },

  updateAdmin: async (email, updates) => {
    try {
      const success = AuthService.updateAdmin(email, updates)
      if (success) {
        console.log('Admin mis à jour avec succès')
      }
      return success
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'admin:', error)
      throw error
    }
  },

  getAdminList: () => {
    try {
      return AuthService.getAdminList()
    } catch (error) {
      console.error('Erreur lors de la récupération de la liste des admins:', error)
      return []
    }
  },

  // Password Management
  changePassword: async (email, oldPassword, newPassword) => {
    try {
      const success = await AuthService.changePassword(email, oldPassword, newPassword)
      if (success) {
        console.log('Mot de passe modifié avec succès')
      }
      return success
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error)
      throw error
    }
  },

  resetAdminPassword: async (adminEmail, newPassword) => {
    try {
      const { user } = get()
      if (!user || !user.email) {
        throw new Error('Session utilisateur non trouvée')
      }
      
      const success = await AuthService.resetAdminPassword(adminEmail, newPassword, user.email)
      if (success) {
        console.log('Mot de passe admin réinitialisé avec succès')
      }
      return success
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe admin:', error)
      throw error
    }
  },

  // Social Media Links
  socialMediaLinks: (() => {
    try {
      const saved = localStorage.getItem('social_media_links')
      return saved ? JSON.parse(saved) : [] // Pas de données par défaut
    } catch {
      return []
    }
  })(),
  updateSocialMediaLinks: (links) => {
    localStorage.setItem('social_media_links', JSON.stringify(links))
    set({ socialMediaLinks: links })
  },
}))

// Selector hooks for better performance
export const useLanguage = () => useStore((state) => state.language)
export const useCustomization = () => useStore((state) => state.customization)
export const useMenuState = () => useStore((state) => ({
  isMenuOpen: state.isMenuOpen,
  toggleMenu: state.toggleMenu,
  closeMenu: state.closeMenu,
}))
export const useModal = () => useStore((state) => ({
  activeModal: state.activeModal,
  modalData: state.modalData,
  openModal: state.openModal,
  closeModal: state.closeModal,
}))
export const useResidenceFilters = () => useStore((state) => ({
  filters: state.residenceFilters,
  setFilters: state.setResidenceFilters,
}))

